import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CostConfirmDialogProps {
  title?: string;
  costUsd: number;
  durationSec?: number;
  currency?: string;
  summary?: string;
  confirmLabel?: string;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CostConfirmDialog({
  title = 'Confirm content generation',
  costUsd,
  durationSec,
  currency = 'USD',
  summary,
  confirmLabel = 'Approve & start',
  pending = false,
  onConfirm,
  onCancel,
}: CostConfirmDialogProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, pending]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => {
        if (!pending) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>

        <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-100">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-semibold tracking-tight">Cost approval required</span>
            <span className="font-mono text-sm text-amber-950 dark:text-amber-50">
              ${costUsd.toFixed(2)} {currency !== 'USD' && currency}
            </span>
          </div>
          {summary && <p className="mt-1 text-amber-900/80 dark:text-amber-100/80">{summary}</p>}
          {durationSec !== undefined && (
            <p className="mt-1 text-amber-900/70 dark:text-amber-100/70">
              Video length: {durationSec}s.
            </p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" disabled={pending} onClick={onConfirm}>
            {pending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
